import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AlertCircle, Lock, Sparkles } from 'lucide-react';
import { Json } from '../types/database.types';
import GoogleGIcon from '../components/GoogleGIcon';
import { Skel, SkeletonRegion } from '../components/ui/PageState';
import {
  mapGoogleAuthError,
  rememberSignupToken,
  setPendingGoogleSignup,
  signInWithGoogle,
} from '../lib/googleAuth';

type SignupAvailability = {
  max: number | null;
  used: number;
  remaining: number | null;
  open: boolean;
  require_token: boolean;
};

function parseSignupAvailability(data: Json | null): SignupAvailability | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const row = data as Record<string, unknown>;
  const remaining = row.remaining;
  return {
    max: typeof row.max === 'number' ? row.max : null,
    used: typeof row.used === 'number' ? row.used : 0,
    remaining: typeof remaining === 'number' ? remaining : null,
    open: row.open === true,
    require_token: row.require_token === true,
  };
}

function signupErrorMessage(raw: string): string {
  if (raw.includes('signup_cap_reached')) return 'O cadastro de novas organizações está encerrado.';
  if (raw.includes('signup_token_required') || raw.includes('signup_token_invalid')) {
    return 'Este link de cadastro não é válido ou já foi usado.';
  }
  return raw;
}

export default function Register() {
  const navigate = useNavigate();
  const { token: tokenParam } = useParams();
  const signupToken = (tokenParam || '').trim();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<SignupAvailability | null>(null);
  const [availabilityLoaded, setAvailabilityLoaded] = useState(false);
  const [linkReason, setLinkReason] = useState<string | null>(null);
  const [linkLoaded, setLinkLoaded] = useState(false);

  useEffect(() => {
    async function loadAvailability() {
      const { data, error: rpcError } = await supabase.rpc('get_signup_availability');
      if (!rpcError) {
        setAvailability(parseSignupAvailability(data));
      }
      setAvailabilityLoaded(true);
    }
    void loadAvailability();
  }, []);

  useEffect(() => {
    if (!availabilityLoaded) return;
    if (!availability?.require_token) {
      setLinkLoaded(true);
      setLinkReason('open');
      return;
    }
    if (!signupToken) {
      setLinkLoaded(true);
      setLinkReason('missing');
      return;
    }
    let cancelled = false;
    setLinkLoaded(false);
    void (async () => {
      const { data, error: peekError } = await supabase.rpc('peek_signup_link', { p_token: signupToken });
      if (cancelled) return;
      if (peekError || !data || typeof data !== 'object' || Array.isArray(data)) {
        setLinkReason('invalid');
      } else {
        const reason = (data as { reason?: unknown }).reason;
        setLinkReason(typeof reason === 'string' ? reason : 'invalid');
      }
      setLinkLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [availabilityLoaded, availability?.require_token, signupToken]);

  const signupClosed = availabilityLoaded && availability?.remaining === 0;
  const requireToken = availability?.require_token === true;
  const linkOk = !requireToken || linkReason === 'ok' || linkReason === 'open';
  const formReady = availabilityLoaded && linkLoaded && !signupClosed && linkOk;
  const busy = loading || googleLoading;
  const limitedSlots =
    availability !== null &&
    availability.max !== null &&
    availability.remaining !== null &&
    availability.remaining > 0;

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupClosed || !formReady) return;
    setLoading(true);
    setError(null);

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          org_name: orgName,
          ...(signupToken ? { signup_token: signupToken } : {}),
        }
      }
    });

    if (error) {
      setError(signupErrorMessage(error.message));
      if (error.message.includes('signup_cap_reached')) {
        setAvailability((prev) => prev ? { ...prev, remaining: 0, open: false } : { max: null, used: 0, remaining: 0, open: false, require_token: true });
      }
      setLoading(false);
    } else {
      navigate('/assinar');
    }
  };

  const handleGoogle = async () => {
    if (signupClosed || !formReady) return;
    const name = orgName.trim();
    if (!name) {
      setError('Informe o nome da organização para continuar com Google.');
      return;
    }

    setGoogleLoading(true);
    setError(null);
    setPendingGoogleSignup({ org_name: name });
    rememberSignupToken(signupToken || null);

    const { error } = await signInWithGoogle(signupToken || undefined);
    if (error) {
      setError(mapGoogleAuthError(error.message).message);
      setGoogleLoading(false);
    }
  };

  const cardState = !availabilityLoaded || !linkLoaded ? 'is-loading' : formReady ? 'is-open' : 'is-closed';
  const closedTitle = signupClosed
    ? 'Cadastro encerrado'
    : linkReason === 'used'
      ? 'Link já usado'
      : linkReason === 'missing'
        ? 'Link de cadastro necessário'
        : 'Link inválido';
  const closedText = signupClosed
    ? 'O período de teste atingiu o limite de organizações. Se você já tem conta, faça login. Convites de equipe continuam funcionando.'
    : linkReason === 'missing'
      ? 'O cadastro é feito por um link exclusivo. Peça o seu para continuar.'
      : linkReason === 'used'
        ? 'Este link de cadastro já foi usado. Peça outro se ainda precisar criar uma organização.'
        : 'Este link de cadastro não é válido. Confira o endereço ou peça um novo.';

  return (
    <div className={`glass-card register-card ${cardState}`}>
      <header className="register-header">
        <div className="auth-brand">
          <img src="/assets/icon-mark.png" alt="Seu.video" className="auth-brand-mark" />
        </div>
        <h1>{formReady ? 'Sua nova organização' : (!availabilityLoaded || !linkLoaded ? 'Sua nova organização' : closedTitle)}</h1>
        <p className="subtitle">
          {formReady || !availabilityLoaded || !linkLoaded
            ? 'Crie projetos e ambientes gerados por IA em segundos.'
            : closedText}
        </p>
        {formReady && limitedSlots && !requireToken && (
          <span className="register-slots-pill">
            <Sparkles size={14} aria-hidden="true" />
            {availability!.remaining === 1
              ? 'Resta 1 vaga'
              : `Restam ${availability!.remaining} vagas`}
          </span>
        )}
      </header>

      {!availabilityLoaded || !linkLoaded ? (
        <SkeletonRegion label="Verificando vagas para novas organizações…">
          <div className="register-status">
            <Skel w="70%" h={44} style={{ borderRadius: 12 }} />
            <Skel w="100%" h={44} style={{ borderRadius: 12 }} />
            <Skel w="100%" h={44} style={{ borderRadius: 12 }} />
          </div>
        </SkeletonRegion>
      ) : null}

      {availabilityLoaded && linkLoaded && !formReady && (
        <div className="register-status">
          <div className="register-status-icon" aria-hidden="true">
            <Lock size={22} />
          </div>
          <p className="register-status-text">
            {signupClosed
              ? 'Recebeu um convite de equipe? Use o link do e-mail para entrar na organização.'
              : 'Se você já tem conta, faça login. Convites de equipe continuam funcionando.'}
          </p>
          <Link to="/login" className="btn-primary auth-callback-primary-link register-status-cta">
            Já tenho conta — fazer login
          </Link>
        </div>
      )}

      {formReady && (
        <>
          {error && (
            <div className="error-message register-error" role="alert">
              <AlertCircle size={18} aria-hidden="true" />
              {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="register-layout register-layout--solo" aria-busy={busy}>
            <div className="register-form">
              <div className="form-group">
                <label htmlFor="register-org">Nome da organização</label>
                <input
                  id="register-org"
                  type="text"
                  required
                  autoComplete="organization"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Minha Imobiliária"
                />
              </div>

              <button
                type="button"
                className="btn-google"
                onClick={handleGoogle}
                disabled={busy}
              >
                <GoogleGIcon />
                {googleLoading ? 'Redirecionando...' : 'Continuar com Google'}
              </button>

              <div className="auth-divider" role="separator">
                <span>ou cadastre com e-mail</span>
              </div>

              <div className="form-group">
                <label htmlFor="register-email">E-mail</label>
                <input
                  id="register-email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="joao@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="register-password">Senha</label>
                <input
                  id="register-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                />
                <span className="form-hint">Mínimo de 6 caracteres.</span>
              </div>

              <button type="submit" className="btn-primary" disabled={busy}>
                {loading ? 'Criando sua conta…' : 'Criar conta e escolher plano'}
              </button>

              <div className="auth-link">
                Já possui uma conta? <Link to="/login">Faça login</Link>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
